import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export function ImpressumPage() {
  useDocumentTitle('Impressum und Datenschutz');
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="max-w-3xl mx-auto py-12 px-4">
      <Link to="/" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium mb-6 inline-block">Zurück zur Startseite</Link>

      <h1 id="impressum" className="text-3xl font-bold text-gray-900 mb-8">Impressum</h1>

      <section className="space-y-6 text-gray-700 text-sm leading-relaxed">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Angaben gemäß § 5 DDG</h2>
          <p>
            Katarzyna Baron<br />
            Kurt-Schumacher-Str. 38<br />
            72762 Reutlingen<br />
            Deutschland
          </p>
        </div>

        <div id="kontakt">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Kontakt</h2>
          <p>E-Mail: <a href="mailto:kontakt@symbolbuch.de" className="text-indigo-600 hover:underline">kontakt@symbolbuch.de</a></p>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Verantwortlich für den Inhalt (§ 18 Abs. 2 MStV)</h2>
          <p>
            <strong>Fachliche Leitung und Inhalt:</strong><br />
            Katarzyna Baron<br />
            Verantwortlich für Konzeption, fachliche Inhalte und UK-bezogene Qualitätssicherung.
          </p>
          <p className="mt-2">
            <strong>Technische Umsetzung und Betrieb:</strong><br />
            Piotr Baron<br />
            Verantwortlich für Architektur, Entwicklung, Hosting und technischen Betrieb.<br />
            <a href="https://baronlab.org" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">baronlab.org</a>
          </p>
        </div>

        <div id="datenschutz">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Datenschutzerklärung</h2>
          <h3 className="font-semibold text-gray-900 mt-4 mb-1">1. Verantwortlicher</h3>
          <p>
            Verantwortlich im Sinne der DSGVO ist Katarzyna Baron (Kontaktdaten siehe oben).
          </p>

          <h3 className="font-semibold text-gray-900 mt-4 mb-1">2. Erhobene Daten</h3>
          <p>
            Zur Nutzung der Anwendung ist eine Registrierung mit E-Mail-Adresse und Name erforderlich.
            Bei der Nutzung werden folgende Daten verarbeitet:
          </p>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>Registrierungsdaten (Name, E-Mail-Adresse)</li>
            <li>Anmeldedaten und Zeitstempel (zur Sicherheit und Nachvollziehbarkeit)</li>
            <li>Selbst erstellte Inhalte (Kommunikationsseiten, hochgeladene Bilder)</li>
            <li>Nutzungspräferenzen (z.&nbsp;B. Dashboard-Einstellungen)</li>
          </ul>

          <h3 className="font-semibold text-gray-900 mt-4 mb-1">2a. Lokale Browserdaten</h3>
          <p>
            Bestimmte Piktogramme (z.&nbsp;B. METACOM-Symbole) werden ausschließlich lokal im Browser
            des Nutzers gespeichert (IndexedDB). Diese Daten verlassen das Endgerät nicht und werden
            nicht an unsere Server übertragen. Die Verantwortung für die Sicherung dieser lokalen Daten
            liegt beim Nutzer.
          </p>

          <h3 className="font-semibold text-gray-900 mt-4 mb-1">3. Zweck der Verarbeitung</h3>
          <p>
            Die Daten werden ausschließlich zum Betrieb der Anwendung verwendet:
            Bereitstellung der Funktionen, Authentifizierung, Speicherung der Nutzerinhalte
            und Verbesserung der Anwendung. Eine Weitergabe an Dritte erfolgt nicht.
          </p>

          <h3 className="font-semibold text-gray-900 mt-4 mb-1">4. Hosting und Speicherort</h3>
          <p>
            Die Anwendung wird auf Servern von Amazon Web Services (AWS) in der EU-Region
            Frankfurt am Main (eu-central-1) betrieben. Alle Daten verbleiben innerhalb der
            Europäischen Union.
          </p>

          <h3 className="font-semibold text-gray-900 mt-4 mb-1">5. Cookies und lokale Speicherung</h3>
          <p>
            Diese Anwendung verwendet ausschließlich technisch notwendige Cookies zur Authentifizierung.
            Es werden keine Tracking-, Analyse- oder Marketing-Cookies eingesetzt.
            Es findet kein Tracking durch Drittanbieter statt.
          </p>
          <p className="mt-2">
            Für lokal gespeicherte Piktogramme nutzt die Anwendung IndexedDB, eine browserseitige
            Datenbank. Diese Daten dienen ausschließlich der Bereitstellung der Piktogramme im Editor
            und werden nicht an Dritte weitergegeben.
          </p>

          <h3 className="font-semibold text-gray-900 mt-4 mb-1">6. Ihre Rechte</h3>
          <p>
            Sie haben gemäß DSGVO das Recht auf Auskunft (Art. 15), Berichtigung (Art. 16),
            Löschung (Art. 17), Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20)
            und Widerspruch (Art. 21). Zur Ausübung dieser Rechte wenden Sie sich an die oben
            genannte Kontaktadresse.
          </p>

          <h3 className="font-semibold text-gray-900 mt-4 mb-1">7. Auftragsverarbeitung</h3>
          <p>
            Mit Amazon Web Services (AWS) besteht ein Auftragsverarbeitungsvertrag (Data Processing Addendum)
            gemäß Art. 28 DSGVO.
          </p>
        </div>

        <div id="nutzungsbedingungen">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Nutzungsbedingungen</h2>
          <p>
            Die Nutzung dieser Webanwendung erfolgt auf eigene Verantwortung. Die Betreiber übernehmen keine Haftung
            für die Richtigkeit, Vollständigkeit oder Aktualität der bereitgestellten Inhalte und Funktionen.
          </p>
          <p className="mt-2">
            Die erstellten Kommunikationsmaterialien dürfen für pädagogische, therapeutische und private Zwecke
            verwendet werden. Eine kommerzielle Weiterverbreitung der über diese Anwendung erstellten Materialien
            ist ohne vorherige schriftliche Zustimmung der Betreiber nicht gestattet.
          </p>
          <p className="mt-2">
            Die Betreiber behalten sich vor, Nutzerkonten bei Verstoß gegen diese Nutzungsbedingungen
            oder bei missbräuchlicher Nutzung zu sperren oder zu löschen.
          </p>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Haftungsausschluss</h2>
          <p>
            Diese Webanwendung unterstützt die Erstellung visueller Kommunikationsmaterialien für den pädagogischen,
            therapeutischen oder privaten Einsatz. Die bereitgestellten Inhalte und Funktionen ersetzen keine individuelle
            fachliche, therapeutische, medizinische oder rechtliche Beratung.
          </p>
          <p className="mt-2">
            Für die Inhalte externer Links übernehmen die Betreiber keine Verantwortung.
            Für den Inhalt der verlinkten Seiten sind ausschließlich deren Betreiber verantwortlich.
          </p>
        </div>

        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Urheberrecht und geistiges Eigentum</h2>
          <p>
            Die Webanwendung Symbolbuch.de, einschließlich ihrer Konzeption, Architektur, Struktur, Gestaltung,
            Benutzeroberfläche, Funktionen, Algorithmen und des gesamten zugrunde liegenden Quellcodes, ist
            urheberrechtlich geschützt gemäß dem deutschen Urheberrechtsgesetz (UrhG) sowie internationalen
            Urheberrechtsabkommen. Alle Rechte liegen bei Katarzyna Baron und Piotr Baron, sofern nicht
            anders gekennzeichnet.
          </p>

          <p className="mt-3 font-semibold text-gray-900">Es ist ausdrücklich untersagt:</p>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>Den Quellcode ganz oder teilweise zu kopieren, dekompilieren, disassemblieren oder in sonstiger Weise rückzuentwickeln (<strong>Reverse Engineering</strong>)</li>
            <li>Die Anwendung, ihre Funktionalität oder wesentliche Teile davon nachzubauen, zu reproduzieren oder als eigenes Produkt zu verbreiten, <strong>auch unter Verwendung von KI-gestützten Werkzeugen</strong></li>
            <li>Die Struktur, das Design, die Benutzerführung oder die fachliche Konzeption der Anwendung zu übernehmen oder nachzuahmen</li>
            <li>Inhalte, Texte, Grafiken oder Designelemente ohne vorherige schriftliche Genehmigung zu verwenden</li>
            <li>Automatisiertes Auslesen (<strong>Scraping</strong>) der Anwendung oder ihrer Inhalte, auch zum Zweck des Trainings von KI-Modellen</li>
          </ul>

          <p className="mt-3">
            Diese Verbote gelten unabhängig davon, ob die Übernahme vollständig oder teilweise erfolgt und
            ob sie manuell oder mithilfe automatisierter Verfahren (einschließlich KI) durchgeführt wird.
          </p>

          <p className="mt-3 font-semibold text-red-700">
            Zuwiderhandlungen werden ohne vorherige Abmahnung zivil- und gegebenenfalls strafrechtlich verfolgt.
            Es gelten die Bestimmungen des deutschen Urheberrechtsgesetzes (UrhG), insbesondere §§ 97 ff. UrhG
            (Schadensersatz und Unterlassung) sowie §§ 106 ff. UrhG (Strafvorschriften).
          </p>
        </div>

        <div id="bildrechte">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Bildrechte und Quellenangaben</h2>
          <p>
            Nutzer sind selbst dafür verantwortlich, dass sie für alle hochgeladenen Bilder und Piktogramme die
            erforderlichen Nutzungsrechte besitzen. Dies gilt insbesondere für lizenzpflichtige Symbolsammlungen
            wie METACOM oder aus dem Internet heruntergeladene Bilder.
          </p>
          <p className="mt-2">
            <strong>METACOM Symbole:</strong> Die Nutzung von METACOM-Symbolen in dieser Anwendung setzt eine
            gültige METACOM-Lizenz voraus, wie sie üblicherweise von Einrichtungen der Behindertenhilfe erworben wird.
            METACOM-Symbole werden ausschließlich lokal im Browser des Nutzers gespeichert und nicht auf unsere
            Server übertragen. Die Erstellung von Kommunikationsseiten mit METACOM-Symbolen ist analog zur Nutzung in
            Präsentationsprogrammen (z.&nbsp;B. PowerPoint) durch die Einrichtungslizenz abgedeckt.
            Weitere Informationen: <a href="https://www.metacom-symbole.de" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">www.metacom-symbole.de</a>
          </p>
          <p className="mt-2">
            Beim PDF-Export werden Quellenangaben automatisch im Footer der generierten Seite eingefügt.
            Nutzer können die Quelle und Lizenz jedes Piktogramms beim Upload oder nachträglich in der Bearbeitungsansicht angeben.
          </p>
        </div>

        <div className="border-t border-gray-200 pt-6 mt-8">
          <p className="text-gray-500 text-xs">© 2026 Katarzyna Baron und Piotr Baron. Alle Rechte vorbehalten.</p>
          <p className="text-gray-400 text-xs mt-1">Stand: April 2026</p>
        </div>
      </section>
    </div>
  );
}
